import { useCallback, useRef, useState } from 'react'
import type { WebrtcVideoConstraints } from '@/api/lessonCalls'

/**
 * Локальный микрофон и камера звонка.
 *
 * Видео включается поверх уже идущего аудио-звонка: дорожка добавляется в
 * существующий поток, соединения переспрашиваются (renegotiation), звонок не
 * прерывается. Ограничения качества приходят с сервера — тариф выбирает
 * профиль, клиент его не назначает.
 */

export type LocalMediaState = {
  micEnabled: boolean
  cameraEnabled: boolean
  screenSharing: boolean
}

function videoConstraints(quality: WebrtcVideoConstraints | null): MediaTrackConstraints {
  if (!quality) return true as unknown as MediaTrackConstraints

  return {
    width: { ideal: quality.width },
    height: { ideal: quality.height },
    frameRate: { ideal: quality.frame_rate },
  }
}

export function useLocalMedia() {
  const streamRef = useRef<MediaStream | null>(null)
  const cameraTrackRef = useRef<MediaStreamTrack | null>(null)
  const screenTrackRef = useRef<MediaStreamTrack | null>(null)
  const [state, setState] = useState<LocalMediaState>({
    micEnabled: true,
    cameraEnabled: false,
    screenSharing: false,
  })

  /** Запросить микрофон (и камеру, если звонок видео). */
  const acquire = useCallback(
    async (options: { video: boolean; quality: WebrtcVideoConstraints | null; deviceId?: string }) => {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          ...(options.deviceId ? { deviceId: { exact: options.deviceId } } : {}),
        },
        video: options.video ? videoConstraints(options.quality) : false,
      })

      streamRef.current = stream
      cameraTrackRef.current = stream.getVideoTracks()[0] ?? null
      setState((prev) => ({ ...prev, micEnabled: true, cameraEnabled: options.video }))

      return stream
    },
    [],
  )

  const getStream = useCallback(() => streamRef.current, [])

  const toggleMic = useCallback(() => {
    const stream = streamRef.current
    if (!stream) return

    const nextEnabled = !stream.getAudioTracks().every((track) => track.enabled)
    stream.getAudioTracks().forEach((track) => {
      track.enabled = nextEnabled
    })
    setState((prev) => ({ ...prev, micEnabled: nextEnabled }))
  }, [])

  /**
   * Включить/выключить камеру в идущем звонке.
   * Возвращает добавленную дорожку (её нужно отдать в соединения) или null.
   */
  const toggleCamera = useCallback(
    async (quality: WebrtcVideoConstraints | null): Promise<MediaStreamTrack | null> => {
      const stream = streamRef.current
      if (!stream) return null

      const existing = cameraTrackRef.current
      if (existing) {
        existing.stop()
        stream.removeTrack(existing)
        cameraTrackRef.current = null
        setState((prev) => ({ ...prev, cameraEnabled: false }))
        return null
      }

      const cameraStream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints(quality),
      })
      const track = cameraStream.getVideoTracks()[0] ?? null
      if (!track) return null

      stream.addTrack(track)
      cameraTrackRef.current = track
      setState((prev) => ({ ...prev, cameraEnabled: true }))

      return track
    },
    [],
  )

  /**
   * Демонстрация экрана: заменяет видеодорожку, не создавая второе соединение.
   * Возвращает новую дорожку для отправки собеседникам.
   *
   * `onEndedExternally` вызывается, когда пользователь останавливает показ
   * кнопкой браузера мимо нашего UI: без этого у собеседника остался бы
   * замерший кадр завершённой дорожки.
   */
  const toggleScreenShare = useCallback(
    async (onEndedExternally?: (fallback: MediaStreamTrack | null) => void): Promise<MediaStreamTrack | null> => {
      if (screenTrackRef.current) {
        screenTrackRef.current.stop()
        screenTrackRef.current = null
        setState((prev) => ({ ...prev, screenSharing: false }))
        return cameraTrackRef.current
      }

      const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true })
      const track = displayStream.getVideoTracks()[0] ?? null
      if (!track) return null

      track.addEventListener('ended', () => {
        screenTrackRef.current = null
        setState((prev) => ({ ...prev, screenSharing: false }))
        onEndedExternally?.(cameraTrackRef.current)
      })

      screenTrackRef.current = track
      setState((prev) => ({ ...prev, screenSharing: true }))

      return track
    },
    [],
  )

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    screenTrackRef.current?.stop()
    streamRef.current = null
    cameraTrackRef.current = null
    screenTrackRef.current = null
    setState({ micEnabled: true, cameraEnabled: false, screenSharing: false })
  }, [])

  return { acquire, getStream, toggleMic, toggleCamera, toggleScreenShare, stop, state }
}
